// src/components/forms/base/flatten-template-model.ts
//
// Pure (framework-free) template-model flattening for the email forms. Extracted
// from `useEmailForm` so it can be unit-tested without pulling the client hook's
// server-action / env import chain. No React, no server imports here.

/**
 * Canonical CAN-SPAM legal entity + Delaware postal address.
 *
 * These MUST match the values pinned in
 * `src/lib/legal/__tests__/entity-consistency.test.ts`. They are the
 * last-resort fallback so a blank env var (`NEXT_PUBLIC_COMPANY_ADDRESS=""`) or
 * a caller passing an empty string can never ship an email footer without the
 * legally-required entity name and physical mailing address.
 */
export const CANONICAL_COMPANY_NAME = "AdSights Inc."
export const CANONICAL_COMPANY_ADDRESS =
  "254 Chapman Rd, Ste 208 #28056, Newark, Delaware 19702"

/**
 * Default template model values used as fallbacks.
 * These are applied only when corresponding fields are undefined.
 */
const DEFAULT_TEMPLATE_VALUES = {
  logo_url:
    process.env.NEXT_PUBLIC_LOGO_URL ??
    "https://www.adsights.ai/logo-square.png",
  product_name: process.env.NEXT_PUBLIC_PRODUCT_NAME ?? "AdSights",
  product_url: process.env.NEXT_PUBLIC_PRODUCT_URL ?? "https://www.adsights.ai",
  support_email: process.env.NEXT_PUBLIC_SUPPORT_EMAIL ?? "support@adsights.ai",
  sender_name: process.env.NEXT_PUBLIC_SENDER_NAME ?? "The AdSights Team",
  company_name: process.env.NEXT_PUBLIC_COMPANY_NAME ?? CANONICAL_COMPANY_NAME,
  company_address:
    process.env.NEXT_PUBLIC_COMPANY_ADDRESS ?? CANONICAL_COMPANY_ADDRESS,
} as const

const DEFAULT_GATED_RESOURCE_VALUES = {
  // Preserve the pre-redesign download copy for existing template/eBook callers.
  // Resource-family-specific sends can still override these fields.
  resource_type_label: "Your download is ready",
  resource_cta_label: "Download your template",
  resource_access_heading: "Your access link",
  resource_delivery_note:
    "Save this email so you can reopen the resource, share it with your team, or revisit the related tools later.",
  resource_usage_heading: "Suggested next step",
  resource_usage_tip:
    "Open the resource while the context is fresh, then duplicate or export a copy before adding campaign-specific data.",
  related_resources_heading: "Recommended next resources",
  share_prompt: "Found this useful? Share it with your team:",
  related_resource_1_name: "Explore more resources",
  related_resource_1_link: "https://www.adsights.ai/resources",
  related_resource_2_name: "Browse marketing calculators",
  related_resource_2_link:
    "https://www.adsights.ai/resources/tools/calculators",
} as const satisfies Record<string, string>

type GatedResourceDefaultKey = keyof typeof DEFAULT_GATED_RESOURCE_VALUES

const DEFAULT_GATED_RESOURCE_KEYS = Object.keys(
  DEFAULT_GATED_RESOURCE_VALUES,
) as GatedResourceDefaultKey[]

const blankToUndefined = (value: unknown): unknown =>
  typeof value === "string" && value.trim().length === 0 ? undefined : value

const defaultIfBlank = (
  record: Record<string, unknown>,
  key: string,
  fallback: string,
): unknown => {
  const value = blankToUndefined(record[key])
  return value === undefined ? fallback : value
}

const stringValue = (value: unknown): string | undefined => {
  const normalized = blankToUndefined(value)
  return typeof normalized === "string" ? normalized : undefined
}

const absoluteHttpsUrl = (value: unknown): string | undefined => {
  const candidate = stringValue(value)
  if (!candidate) return undefined
  try {
    const url = new URL(candidate)
    return url.protocol === "https:" ? url.toString() : undefined
  } catch {
    return undefined
  }
}

const shareableResourceUrl = (value: unknown): string | undefined => {
  const url = absoluteHttpsUrl(value)
  if (!url) return undefined
  const { pathname } = new URL(url)
  return /\.(?:csv|pdf|xlsx?|zip|docx?|pptx?)$/i.test(pathname)
    ? undefined
    : url
}

/**
 * Helper function to convert any object to a Record<string, unknown>
 * while preserving full URLs and undefined values.
 *
 * @param {unknown} obj - Object to convert to record
 * @returns {Record<string, unknown>} A clean record with preserved values
 */
export const toRecord = (obj: unknown): Record<string, unknown> => {
  if (typeof obj !== "object" || obj === null) return {}
  return Object.entries(obj as Record<string, unknown>).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: value === undefined ? undefined : value,
    }),
    {},
  )
}

/**
 * Template model structure with all possible template types.
 * This interface organizes template data into logical sections based on template type,
 * allowing for structured composition of email templates with proper type safety.
 *
 * @interface TemplateModelStructure
 * @property {Record<string, unknown>} [base] - Base template fields common to all templates
 * @property {Record<string, unknown>} [gatedResource] - Gated resource template specific fields
 * @property {Record<string, unknown>} [gatedContentNoDownload] - Gated content without download template specific fields
 * @property {Record<string, unknown>} [resourceCenterToolResults] - Resource center tool results template specific fields
 * @property {Record<string, unknown>} [newsletter] - Newsletter template specific fields
 * @property {Record<string, unknown>} [custom] - Custom template fields that override defaults
 */
export interface TemplateModelStructure {
  /** Base template fields common to all templates */
  base?: Record<string, unknown>
  /** Gated resource template specific fields */
  gatedResource?: Record<string, unknown>
  /** Gated content without download template specific fields */
  gatedContentNoDownload?: Record<string, unknown>
  /** Resource center tool results template specific fields */
  resourceCenterToolResults?: Record<string, unknown>
  /** Newsletter template specific fields */
  newsletter?: Record<string, unknown>
  /** Early access application template specific fields */
  earlyAccessApplication?: Record<string, unknown>
  /** Custom template fields that override defaults */
  custom?: Record<string, unknown>
}

/**
 * Coalesce the CAN-SPAM footer identity fields on a fully-merged model so a
 * blank (empty/whitespace) `company_name`/`company_address` — from a blank env
 * default, a stale caller `base`, or a `custom` override — falls back to the
 * canonical entity + Delaware address instead of shipping a blank footer.
 * Mirrors the `defaultIfBlank` precedent already used for the gated-resource
 * copy keys below.
 */
const withCanSpamDefaults = (
  record: Record<string, unknown>,
): Record<string, unknown> => ({
  ...record,
  company_name: defaultIfBlank(record, "company_name", CANONICAL_COMPANY_NAME),
  company_address: defaultIfBlank(
    record,
    "company_address",
    CANONICAL_COMPANY_ADDRESS,
  ),
})

/**
 * Flattens a nested template model into a single-level object for email service providers
 * and applies default values for missing fields based on template type.
 *
 * This function handles the merging strategy for different template types:
 * 1. Applies default values from DEFAULT_TEMPLATE_VALUES for undefined base fields
 * 2. Merges template-specific fields based on the provided template type
 * 3. Applies custom fields with highest priority to override any defaults
 * 4. Coalesces blank CAN-SPAM footer identity fields to the canonical defaults
 *
 * @param {TemplateModelStructure} templateModel - The structured template model with separate sections
 * @returns {Record<string, unknown>} A flattened record with all template values merged according to priority
 */
export const flattenTemplateModel = (
  templateModel: TemplateModelStructure,
): Record<string, unknown> => {
  const {
    base = {},
    gatedResource,
    gatedContentNoDownload,
    resourceCenterToolResults,
    newsletter,
    custom = {},
  } = templateModel

  // Convert objects while preserving URLs and undefined values
  const baseRecord = toRecord(base)
  const gatedRecord = gatedResource ? toRecord(gatedResource) : undefined
  const gatedContentNoDownloadRecord = gatedContentNoDownload
    ? toRecord(gatedContentNoDownload)
    : undefined
  const toolResultsRecord = resourceCenterToolResults
    ? toRecord(resourceCenterToolResults)
    : undefined
  const newsletterRecord = newsletter ? toRecord(newsletter) : undefined
  const customRecord = toRecord(custom)

  // Apply defaults only to undefined fields
  const baseWithDefaults: Record<string, unknown> = {
    ...Object.entries(DEFAULT_TEMPLATE_VALUES).reduce<Record<string, unknown>>(
      (acc, [key, defaultValue]) => ({
        ...acc,
        [key]: baseRecord[key] === undefined ? defaultValue : baseRecord[key],
      }),
      {},
    ),
    ...baseRecord,
  }

  // Handle each template type with proper merging strategy

  // For gated resources, merge base fields into gated resource
  if (gatedRecord) {
    // Apply defaults to gated resource fields only if they're undefined
    const gatedWithDefaults: Record<string, unknown> = {
      ...Object.entries(DEFAULT_TEMPLATE_VALUES).reduce<
        Record<string, unknown>
      >(
        (acc, [key, defaultValue]) => ({
          ...acc,
          [key]:
            gatedRecord[key] === undefined ? defaultValue : gatedRecord[key],
        }),
        {},
      ),
      ...gatedRecord,
      ...DEFAULT_GATED_RESOURCE_KEYS.reduce<Record<string, unknown>>(
        (acc, key) => ({
          ...acc,
          [key]: defaultIfBlank(
            gatedRecord,
            key,
            DEFAULT_GATED_RESOURCE_VALUES[key],
          ),
        }),
        {},
      ),
    }

    const shareUrl =
      shareableResourceUrl(gatedWithDefaults.resource_share_url) ??
      shareableResourceUrl(gatedWithDefaults.resource_preview_link) ??
      shareableResourceUrl(baseWithDefaults.product_url)
    if (blankToUndefined(gatedWithDefaults.resource_icon_url) === undefined) {
      gatedWithDefaults.resource_icon_url =
        absoluteHttpsUrl(baseWithDefaults.logo_url) ??
        DEFAULT_TEMPLATE_VALUES.logo_url
    }
    if (
      shareUrl &&
      blankToUndefined(gatedWithDefaults.twitter_share_url) === undefined
    ) {
      gatedWithDefaults.twitter_share_url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}`
    }
    if (
      shareUrl &&
      blankToUndefined(gatedWithDefaults.linkedin_share_url) === undefined
    ) {
      gatedWithDefaults.linkedin_share_url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    }

    return withCanSpamDefaults({
      ...baseWithDefaults,
      ...gatedWithDefaults,
      ...customRecord,
    })
  }

  // For gated content no download, merge base fields with gated content no download fields
  if (gatedContentNoDownloadRecord) {
    return withCanSpamDefaults({
      ...baseWithDefaults,
      ...gatedContentNoDownloadRecord,
      ...customRecord,
    })
  }

  // For tool results, merge base fields with tool results fields
  if (toolResultsRecord) {
    return withCanSpamDefaults({
      ...baseWithDefaults,
      ...toolResultsRecord,
      ...customRecord,
    })
  }

  // For newsletters, merge base fields with newsletter fields
  if (newsletterRecord) {
    return withCanSpamDefaults({
      ...baseWithDefaults,
      ...newsletterRecord,
      ...customRecord,
    })
  }

  // For regular templates (welcome), just merge base and custom
  return withCanSpamDefaults({
    ...baseWithDefaults,
    ...customRecord,
  })
}
